import React from 'react'
import axios from 'axios'
import { addDays, format } from 'date-fns'
import {
    APIProvider,
    AdvancedMarker,
    InfoWindow,
    Map as GoogleMap,
    Pin,
} from '@vis.gl/react-google-maps'
import {
    MdArrowBack,
    MdCheck,
    MdClose,
    MdOutlineSchool,
} from 'react-icons/md'
import TeacherDriveTimeBubble from '../../AdminWorkflows/TempTeacherDistancePopup/TeacherDriveTimeBubble/TeacherDriveTimeBubble'
import { createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime } from '../../Calendar/calendarFunctions'
import { baseUrl } from '../../assets/Animations'
import { getRequestHeaders } from '../../api'
import StudentBadge from '../FamilyInfoGutter/StudentBadge/StudentBadge'
import MagicSchedulerCalendar from './MagicSchedulerCalendar/MagicSchedulerCalendar'
import {
    AVAILABILITY_DAYS,
    AVAILABILITY_PERIODS,
} from '../FamilyInfoGutter/ContactInfoTab/contactInfoCopyUtils'
import {
    createTeacherColorMap,
    findMarkerForSuggestion,
    formatMapAddress,
    getAvailabilityMatchingTeacherKeys,
    getLocationFromRecord,
    getSuggestionKey,
    getSuggestionTeacherKey,
    getSuggestionObject,
    getTeacherHomeMarkerId,
    getUniqueSuggestedTeachers,
    isValidMapLocation,
    MAGIC_SCHEDULER_FAMILY_BORDER_COLOR,
    MAGIC_SCHEDULER_FAMILY_COLOR,
} from './magicSchedulerUtils'
import './magic-scheduler.css'

const GOOGLE_MAPS_API_KEY = 'AIzaSyBLfMu_7gTWuHw6C3kOqLB8uBUjP9eNLF8'
const GOOGLE_MAP_ID = '3324feda48bc0f19'
const DEFAULT_MAP_CENTER = { lat: 40.015, lng: -105.2705 }
const EMPTY_TEACHERS = []
const EMPTY_AVAILABILITY = {}
const LEGACY_BROWSER_GEOCODE_CACHE_KEY = 'atharo.magic-scheduler.geocode-cache.v1'

const getStudentDisplayName = (event) => String(
    event?.studentName
    || event?.StudentName
    || event?.name
    || 'Student'
).trim()

const getEventScheduleLabel = (event) => {
    if (event?.day && event?.time) return `${event.day} ${event.time}`
    if (!event?.dateTime) return ''
    const date = new Date(event.dateTime)
    if (Number.isNaN(date.getTime())) return ''
    return `${format(date, 'EEE')} ${format(date, 'h:mm a')}`
}

const getRosterEvents = (rawEvents) => {
    const displayedDays = Array.from({ length: 61 }, (_, index) => addDays(new Date(), index))
    let expandedCalendar = {}

    createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
        displayedDays,
        (Array.isArray(rawEvents) ? rawEvents : []).filter((event) => !event?.isPendingOffer),
        (result) => {
            expandedCalendar = result || {}
        }
    )

    return displayedDays.flatMap(
        (day) => expandedCalendar?.[format(day, 'yyyy-MM-dd')]?.events || []
    )
}

const buildTeacherStops = (teacher, rawEvents) => {
    const stops = {}
    getRosterEvents(rawEvents).forEach((event) => {
        const address = event?.familyAddress || event?.address || {}
        const addressLabel = formatMapAddress(address)
        const familyId = String(event?.familyId || '').trim()
        if (!addressLabel && !getLocationFromRecord(event)) return

        const stopIdentity = familyId || addressLabel.toLowerCase()
        const stopKey = `${teacher.key}:${stopIdentity}`
        if (!stops[stopKey]) {
            stops[stopKey] = {
                id: `student-stop-${stopKey}`,
                type: 'student-stop',
                teacherKey: teacher.key,
                teacherId: teacher.teacherId,
                teacherName: teacher.teacherName,
                color: teacher.color,
                familyId,
                familyName: event?.familyName || 'Family',
                address: addressLabel,
                location: getLocationFromRecord(event),
                studentsByKey: {},
            }
        }

        const studentName = getStudentDisplayName(event)
        const studentKey = String(event?.studentId || studentName).trim()
        if (!stops[stopKey].studentsByKey[studentKey]) {
            stops[stopKey].studentsByKey[studentKey] = {
                id: studentKey,
                name: studentName,
                scheduleLabels: new Set(),
            }
        }
        const scheduleLabel = getEventScheduleLabel(event)
        if (scheduleLabel) {
            stops[stopKey].studentsByKey[studentKey].scheduleLabels.add(scheduleLabel)
        }
    })

    return Object.values(stops).map((stop) => ({
        ...stop,
        students: Object.values(stop.studentsByKey).map((student) => ({
            ...student,
            scheduleLabels: Array.from(student.scheduleLabels).sort(),
        })),
    }))
}

const MagicScheduler = ({
    modalInfo,
    familyProfile,
    teacherDistanceSuggestions = [],
    setTeacherDistanceSuggestions = () => {},
    teachers = EMPTY_TEACHERS,
    userData = { Name: 'Admin' },
    socket,
    isOpen = true,
    onClose,
}) => {
    const [activeTab, setActiveTab] = React.useState('calendar')
    const [hasOpenedCalendar, setHasOpenedCalendar] = React.useState(true)
    const [suggestions, setSuggestions] = React.useState(
        Array.isArray(teacherDistanceSuggestions) ? teacherDistanceSuggestions : []
    )
    const [invitations, setInvitations] = React.useState([])
    const [suggestionsLoading, setSuggestionsLoading] = React.useState(
        !Array.isArray(teacherDistanceSuggestions) || teacherDistanceSuggestions.length === 0
    )
    const [suggestionsError, setSuggestionsError] = React.useState('')
    const [mapsReady, setMapsReady] = React.useState(false)
    const [mapMarkers, setMapMarkers] = React.useState([])
    const [mapLoading, setMapLoading] = React.useState(true)
    const [mapError, setMapError] = React.useState('')
    const [selectedMarkerId, setSelectedMarkerId] = React.useState(null)
    const [selectedPinSuggestionKey, setSelectedPinSuggestionKey] = React.useState(null)
    const [hoveredMarkerId, setHoveredMarkerId] = React.useState(null)
    const [hoveredSuggestionKey, setHoveredSuggestionKey] = React.useState(null)
    const [filteredTeacherKey, setFilteredTeacherKey] = React.useState('')
    const [lockedMarkerId, setLockedMarkerId] = React.useState(null)
    const [calendarSelection, setCalendarSelection] = React.useState(null)
    const [availabilityMatchOn, setAvailabilityMatchOn] = React.useState(true)
    const suggestionBubbleRefs = React.useRef(new Map())
    const suggestionListRef = React.useRef(null)
    const markerHoverBindings = React.useRef(new Map())
    const activeHoveredMarkerRef = React.useRef(null)
    const wasOpenRef = React.useRef(isOpen)

    React.useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            setActiveTab('calendar')
            setHasOpenedCalendar(true)
            setCalendarSelection(null)
            setSelectedPinSuggestionKey(null)
            setLockedMarkerId(null)
            setSelectedMarkerId(null)
        }
        wasOpenRef.current = isOpen
    }, [isOpen])

    const familyName = familyProfile?.familyName || modalInfo?.todoName || 'Family'
    const familyAddress = React.useMemo(
        () => formatMapAddress(familyProfile?.address),
        [familyProfile?.address]
    )
    const savedFamilyLocation = React.useMemo(
        () => getLocationFromRecord(familyProfile || {})
            || getLocationFromRecord(familyProfile?.address || {}),
        [familyProfile]
    )
    const familyMessage = String(
        familyProfile?.initialAvailabilityNotes
        || modalInfo?.todoDescription
        || ''
    ).trim()
    const familyAvailability = familyProfile?.initialAvailability || EMPTY_AVAILABILITY
    const colorMap = React.useMemo(() => createTeacherColorMap(suggestions), [suggestions])
    const suggestedTeachers = React.useMemo(
        () => getUniqueSuggestedTeachers(suggestions),
        [suggestions]
    )
    const availabilityMatchingTeacherKeys = React.useMemo(
        () => getAvailabilityMatchingTeacherKeys(
            suggestions,
            teachers,
            familyAvailability
        ),
        [familyAvailability, suggestions, teachers]
    )
    const availabilityFilteredSuggestions = React.useMemo(
        () => availabilityMatchOn
            ? suggestions.filter((suggestion) =>
                availabilityMatchingTeacherKeys.has(getSuggestionTeacherKey(suggestion))
            )
            : suggestions,
        [availabilityMatchOn, availabilityMatchingTeacherKeys, suggestions]
    )
    const visibleSuggestions = React.useMemo(
        () => filteredTeacherKey
            ? availabilityFilteredSuggestions.filter(
                (suggestion) => getSuggestionTeacherKey(suggestion) === filteredTeacherKey
            )
            : availabilityFilteredSuggestions,
        [availabilityFilteredSuggestions, filteredTeacherKey]
    )

    React.useEffect(() => {
        if (
            !availabilityMatchOn
            || !filteredTeacherKey
            || availabilityMatchingTeacherKeys.has(filteredTeacherKey)
        ) return

        setFilteredTeacherKey('')
        setLockedMarkerId(null)
        setSelectedMarkerId(null)
        setHoveredMarkerId(null)
        setHoveredSuggestionKey(null)
    }, [availabilityMatchOn, availabilityMatchingTeacherKeys, filteredTeacherKey])

    React.useEffect(() => {
        try {
            window.localStorage.removeItem(LEGACY_BROWSER_GEOCODE_CACHE_KEY)
        } catch (error) {
            // Browser storage may be unavailable; the scheduler now uses the server cache.
        }
    }, [])

    React.useEffect(() => {
        if (!isOpen) return undefined
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', closeOnEscape)
        return () => window.removeEventListener('keydown', closeOnEscape)
    }, [isOpen, onClose])

    React.useEffect(() => {
        let isActive = true

        const loadSuggestions = async () => {
            setSuggestionsError('')
            try {
                const invitationRequest = axios.get(
                    `${baseUrl}/teacher-invitations/${modalInfo?.familyId}`,
                    getRequestHeaders()
                ).catch((error) => {
                    console.error('Unable to load teacher invitations for the scheduler:', error)
                    return { data: [] }
                })

                let nextSuggestions = suggestions
                if (nextSuggestions.length === 0) {
                    setSuggestionsLoading(true)
                    const suggestionResponse = await axios.get(
                        `${baseUrl}/geolocation/${modalInfo?.familyId}/${String(modalInfo?.whichSchool || '').toLowerCase()}/`
                    )
                    nextSuggestions = (Array.isArray(suggestionResponse?.data) ? suggestionResponse.data : [])
                        .filter((suggestion) => Number(suggestion?.sortingDuration) < 60)
                        .sort((left, right) =>
                            Number(left?.sortingDuration || Infinity)
                            - Number(right?.sortingDuration || Infinity)
                        )
                }

                const invitationResponse = await invitationRequest
                if (!isActive) return
                setSuggestions(nextSuggestions)
                setTeacherDistanceSuggestions(nextSuggestions)
                setInvitations(Array.isArray(invitationResponse?.data) ? invitationResponse.data : [])
            } catch (error) {
                console.error('Unable to load Magic Scheduler suggestions:', error)
                if (isActive) {
                    setSuggestionsError('Unable to load scheduling suggestions right now.')
                }
            } finally {
                if (isActive) setSuggestionsLoading(false)
            }
        }

        loadSuggestions()
        return () => {
            isActive = false
        }
        // Suggestions are intentionally loaded once for the open family.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalInfo?.familyId, modalInfo?.whichSchool])

    React.useEffect(() => {
        if (mapsReady) return undefined
        const checkMaps = () => {
            if (window.google?.maps?.Geocoder) {
                setMapsReady(true)
                return true
            }
            return false
        }
        if (checkMaps()) return undefined
        const intervalId = window.setInterval(() => {
            if (checkMaps()) window.clearInterval(intervalId)
        }, 250)
        return () => window.clearInterval(intervalId)
    }, [mapsReady])

    const geocodeAddress = React.useCallback(async (address) => {
        if (!address) return null
        try {
            const response = await axios.post(
                `${baseUrl}/scheduler-geocodes/resolve`,
                { address },
                getRequestHeaders()
            )
            const lat = Number(response?.data?.lat)
            const lng = Number(response?.data?.lng)
            return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
        } catch (error) {
            console.error('Unable to resolve scheduler address:', error)
            return null
        }
    }, [])

    const teacherRosterKey = React.useMemo(
        () => suggestedTeachers.map((teacher) => `${teacher.key}:${teacher.teacherId}`).join('|'),
        [suggestedTeachers]
    )

    React.useEffect(() => {
        if (!mapsReady || suggestionsLoading) return undefined

        let isActive = true
        const loadMapMarkers = async () => {
            setMapLoading(true)
            setMapError('')
            try {
                const rosterResults = await Promise.all(
                    suggestedTeachers.map(async (teacher) => {
                        if (!teacher.teacherId) return []
                        try {
                            const response = await axios.get(
                                `${baseUrl}/calendar/teacher/${teacher.teacherId}`,
                                getRequestHeaders()
                            )
                            return buildTeacherStops(teacher, response?.data)
                        } catch (error) {
                            console.error(`Unable to load ${teacher.teacherName}'s roster:`, error)
                            return []
                        }
                    })
                )

                const rawStops = rosterResults.flat()
                const teacherHomes = suggestedTeachers.map((teacher) => {
                    const suggestionObject = getSuggestionObject(teacher.suggestion)
                    const teacherProfile = (Array.isArray(teachers) ? teachers : []).find(
                        (candidate) => String(candidate?.TeacherID || '').trim() === teacher.teacherId
                    )
                    const addressRecord = teacherProfile?.Address
                        || suggestionObject?.Address
                        || suggestionObject?.address
                        || {}
                    const instrumentList = teacherProfile?.InstrumentList
                        || suggestionObject?.InstrumentList
                        || suggestionObject?.instrumentList
                        || []
                    return {
                        id: getTeacherHomeMarkerId(teacher.key),
                        type: 'teacher-home',
                        teacherKey: teacher.key,
                        teacherId: teacher.teacherId,
                        teacherName: teacher.teacherName,
                        color: teacher.color,
                        address: formatMapAddress(addressRecord),
                        location: getLocationFromRecord(teacherProfile || suggestionObject)
                            || getLocationFromRecord(addressRecord),
                        instruments: (Array.isArray(instrumentList) ? instrumentList : [])
                            .map((instrument) => instrument?.name || instrument?.parentInstrument || instrument)
                            .filter(Boolean),
                    }
                })
                const resolveLocation = async (stop) => {
                    if (isValidMapLocation(stop.location)) return stop.location
                    if (!stop.address) return null
                    return geocodeAddress(stop.address)
                }

                const [familyLocation, studentStops, resolvedTeacherHomes] = await Promise.all([
                    isValidMapLocation(savedFamilyLocation)
                        ? Promise.resolve(savedFamilyLocation)
                        : familyAddress ? geocodeAddress(familyAddress) : Promise.resolve(null),
                    Promise.all(rawStops.map(async (stop) => ({
                        ...stop,
                        location: await resolveLocation(stop),
                    }))),
                    Promise.all(teacherHomes.map(async (teacherHome) => ({
                        ...teacherHome,
                        location: await resolveLocation(teacherHome),
                    }))),
                ])

                if (!isActive) return
                const nextMarkers = studentStops.filter((stop) => isValidMapLocation(stop.location))
                nextMarkers.push(
                    ...resolvedTeacherHomes.filter((marker) => isValidMapLocation(marker.location))
                )
                if (isValidMapLocation(familyLocation)) {
                    nextMarkers.unshift({
                        id: `target-family-${modalInfo?.familyId || modalInfo?.todoId}`,
                        type: 'target-family',
                        familyName,
                        address: familyAddress,
                        location: familyLocation,
                        color: MAGIC_SCHEDULER_FAMILY_COLOR,
                    })
                }
                setMapMarkers(nextMarkers)
                if (nextMarkers.length === 0) {
                    setMapError('No mappable student or family addresses were found.')
                }
            } catch (error) {
                console.error('Unable to build Magic Scheduler map:', error)
                if (isActive) setMapError('Unable to load the scheduler map right now.')
            } finally {
                if (isActive) setMapLoading(false)
            }
        }

        loadMapMarkers()
        return () => {
            isActive = false
        }
    }, [familyAddress, familyName, geocodeAddress, mapsReady, modalInfo?.familyId, modalInfo?.todoId, savedFamilyLocation, suggestionsLoading, suggestedTeachers, teacherRosterKey, teachers])

    const visibleMapMarkers = React.useMemo(
        () => mapMarkers.filter((marker) => {
            if (marker.type === 'target-family') return true
            if (
                availabilityMatchOn
                && !availabilityMatchingTeacherKeys.has(marker.teacherKey)
            ) {
                return false
            }
            return !filteredTeacherKey || marker.teacherKey === filteredTeacherKey
        }),
        [availabilityMatchOn, availabilityMatchingTeacherKeys, filteredTeacherKey, mapMarkers]
    )

    const focusedMarker = React.useMemo(
        () => visibleMapMarkers.find(
            (marker) => marker.id === (hoveredMarkerId || selectedMarkerId || lockedMarkerId)
        ) || null,
        [hoveredMarkerId, lockedMarkerId, selectedMarkerId, visibleMapMarkers]
    )

    const mapCenter = React.useMemo(() => {
        if (filteredTeacherKey) {
            const teacherHome = visibleMapMarkers.find((marker) => marker.type === 'teacher-home')
            if (teacherHome) return teacherHome.location
        }
        const familyMarker = visibleMapMarkers.find((marker) => marker.type === 'target-family')
        return familyMarker?.location || visibleMapMarkers[0]?.location || DEFAULT_MAP_CENTER
    }, [filteredTeacherKey, visibleMapMarkers])

    const selectedMarker = React.useMemo(
        () => visibleMapMarkers.find((marker) => marker.id === selectedMarkerId) || null,
        [selectedMarkerId, visibleMapMarkers]
    )

    const filteredTeacher = suggestedTeachers.find(
        (teacher) => teacher.key === filteredTeacherKey
    )

    const suggestionTargetByKey = React.useMemo(() => {
        const targets = {}
        suggestions.forEach((suggestion, index) => {
            const suggestionKey = getSuggestionKey(suggestion, index)
            targets[suggestionKey] = findMarkerForSuggestion(suggestion, mapMarkers)?.id || null
        })
        return targets
    }, [mapMarkers, suggestions])

    const visibleSuggestionMarkerIds = React.useMemo(() => {
        const visibleSuggestionKeys = new Set(
            visibleSuggestions.map((suggestion) =>
                getSuggestionKey(suggestion, suggestions.indexOf(suggestion))
            )
        )
        return new Set(
            Object.entries(suggestionTargetByKey)
                .filter(([suggestionKey, markerId]) => (
                    markerId && visibleSuggestionKeys.has(suggestionKey)
                ))
                .map(([, markerId]) => markerId)
        )
    }, [suggestionTargetByKey, suggestions, visibleSuggestions])

    const handleSuggestionHover = (suggestion, index) => {
        const suggestionKey = getSuggestionKey(suggestion, index)
        setHoveredSuggestionKey(suggestionKey)
        setHoveredMarkerId(suggestionTargetByKey[suggestionKey] || null)
    }

    const clearSuggestionHover = () => {
        setHoveredSuggestionKey(null)
        setHoveredMarkerId(null)
    }

    const handleSuggestionSelect = (suggestion, index) => {
        const teacherKey = getSuggestionTeacherKey(suggestion)
        const suggestionKey = getSuggestionKey(suggestion, index)
        const suggestionObject = getSuggestionObject(suggestion)
        const isCalendarEvent = suggestionObject.type === 'calendarEvent'

        setCalendarSelection({
            suggestion: isCalendarEvent ? suggestion : null,
            suggestionKey,
            teacherKey,
        })
        setSelectedPinSuggestionKey(suggestionKey)
        setLockedMarkerId(suggestionTargetByKey[suggestionKey] || null)
        setHasOpenedCalendar(true)
        setActiveTab('calendar')
    }

    const toggleAvailabilityMatch = () => {
        const nextAvailabilityMatchOn = !availabilityMatchOn
        setAvailabilityMatchOn(nextAvailabilityMatchOn)
        if (
            nextAvailabilityMatchOn
            && filteredTeacherKey
            && !availabilityMatchingTeacherKeys.has(filteredTeacherKey)
        ) {
            setFilteredTeacherKey('')
            setLockedMarkerId(null)
            setSelectedMarkerId(null)
        }
        setHoveredMarkerId(null)
        setHoveredSuggestionKey(null)
    }

    const scrollToMarkerBubble = (markerId) => {
        const matchingBubbleEntry = Object.entries(suggestionTargetByKey).find(
            ([suggestionKey, targetMarkerId]) => (
                targetMarkerId === markerId
                && suggestionBubbleRefs.current.has(suggestionKey)
            )
        )
        const bubble = matchingBubbleEntry
            ? suggestionBubbleRefs.current.get(matchingBubbleEntry[0])
            : null
        const suggestionList = suggestionListRef.current
        if (bubble && suggestionList?.scrollTo) {
            const listBounds = suggestionList.getBoundingClientRect()
            const bubbleBounds = bubble.getBoundingClientRect()
            const centeredTop = suggestionList.scrollTop
                + bubbleBounds.top
                - listBounds.top
                - ((suggestionList.clientHeight - bubbleBounds.height) / 2)
            suggestionList.scrollTo({
                top: Math.max(0, centeredTop),
                behavior: 'smooth',
            })
        } else if (bubble?.scrollIntoView) {
            bubble.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
        return matchingBubbleEntry?.[0] || null
    }

    const handleMarkerHover = (markerId) => {
        if (activeHoveredMarkerRef.current === markerId) return
        activeHoveredMarkerRef.current = markerId
        setHoveredSuggestionKey(null)
        setHoveredMarkerId(markerId)
        scrollToMarkerBubble(markerId)
    }

    const handleMarkerClick = (markerId) => {
        setSelectedMarkerId(markerId)
        const suggestionKey = scrollToMarkerBubble(markerId)
        setSelectedPinSuggestionKey(suggestionKey)
        if (!suggestionKey) return

        const suggestionIndex = suggestions.findIndex(
            (suggestion, index) => getSuggestionKey(suggestion, index) === suggestionKey
        )
        if (suggestionIndex >= 0) {
            handleSuggestionSelect(suggestions[suggestionIndex], suggestionIndex)
        }
    }

    const clearMarkerHover = (markerId) => {
        if (activeHoveredMarkerRef.current === markerId) {
            activeHoveredMarkerRef.current = null
        }
        setHoveredMarkerId((currentId) => (
            currentId === markerId ? null : currentId
        ))
    }

    const bindMarkerHover = (markerId, markerElement) => {
        const previousBinding = markerHoverBindings.current.get(markerId)
        if (previousBinding) {
            previousBinding.target.removeEventListener('pointerenter', previousBinding.enter)
            previousBinding.target.removeEventListener('pointerleave', previousBinding.leave)
            markerHoverBindings.current.delete(markerId)
        }
        if (!markerElement) return

        const target = markerElement.content || markerElement.element || markerElement
        if (!target?.addEventListener) return
        const enter = () => handleMarkerHover(markerId)
        const leave = () => clearMarkerHover(markerId)
        target.addEventListener('pointerenter', enter)
        target.addEventListener('pointerleave', leave)
        markerHoverBindings.current.set(markerId, { target, enter, leave })
    }

    React.useEffect(() => () => {
        markerHoverBindings.current.forEach(({ target, enter, leave }) => {
            target.removeEventListener('pointerenter', enter)
            target.removeEventListener('pointerleave', leave)
        })
        markerHoverBindings.current.clear()
    }, [])

    React.useEffect(() => {
        if (!hoveredMarkerId) return
        if (!visibleMapMarkers.some((marker) => marker.id === hoveredMarkerId)) {
            setHoveredMarkerId(null)
            setHoveredSuggestionKey(null)
        }
    }, [hoveredMarkerId, visibleMapMarkers])

    React.useEffect(() => {
        if (!selectedMarkerId) setSelectedPinSuggestionKey(null)
    }, [selectedMarkerId])

    const studentList = (modalInfo?.studentList || []).map((student) => ({
        ...student,
        instrumentList: Array.isArray(student?.instrumentList) ? student.instrumentList : [],
    }))
    const safeFamilyProfile = {
        ...(familyProfile || {}),
        students: Array.isArray(familyProfile?.students) && familyProfile.students.length > 0
            ? familyProfile.students
            : studentList,
    }

    return (
        <div
            className={`magic-scheduler__overlay${isOpen ? '' : ' is-hidden'}`}
            aria-hidden={!isOpen}
            onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose()
            }}
        >
            <button
                type='button'
                className='magic-scheduler__back'
                aria-label='Close scheduler'
                onClick={onClose}
            >
                <MdArrowBack size={24} aria-hidden='true' />
            </button>
            <div className='magic-scheduler__modal'>
                <header className='magic-scheduler__header'>
                    <div className='magic-scheduler__identity'>
                        <div className='magic-scheduler__eyebrow'>Magic Scheduler</div>
                        <h2>{familyName}</h2>
                    </div>
                    <nav className='magic-scheduler__tabs' aria-label='Scheduler views'>
                        <button
                            type='button'
                            className={activeTab === 'calendar' ? 'is-active' : ''}
                            onClick={() => {
                                setHasOpenedCalendar(true)
                                setActiveTab('calendar')
                            }}
                        >
                            Calendar
                        </button>
                        <button
                            type='button'
                            className={activeTab === 'suggestions' ? 'is-active' : ''}
                            onClick={() => setActiveTab('suggestions')}
                        >
                            Map
                            <span>{visibleSuggestions.length}</span>
                        </button>
                    </nav>
                    <button
                        type='button'
                        className='magic-scheduler__close'
                        aria-label='Close scheduler'
                        onClick={onClose}
                    >
                        <MdClose size={20} aria-hidden='true' />
                    </button>
                </header>

                <div className='magic-scheduler__workspace'>
                        <aside className='magic-scheduler__suggestions'>
                            <button
                                type='button'
                                className={`magic-scheduler__availability-match${availabilityMatchOn ? ' is-on' : ' is-off'}`}
                                role='switch'
                                aria-checked={availabilityMatchOn}
                                aria-label='Availability Match'
                                onClick={toggleAvailabilityMatch}
                            >
                                <span className='magic-scheduler__availability-match-track'>
                                    <span />
                                </span>
                                Availability Match
                            </button>
                            {filteredTeacher && (
                                <button
                                    type='button'
                                    className='magic-scheduler__teacher-filter'
                                    style={{ '--teacher-filter-color': filteredTeacher.color }}
                                    onClick={() => {
                                        setFilteredTeacherKey('')
                                        setLockedMarkerId(null)
                                        setHoveredMarkerId(null)
                                        setHoveredSuggestionKey(null)
                                    }}
                                >
                                    <span />
                                    Showing {filteredTeacher.teacherName}
                                    <MdClose size={14} aria-hidden='true' />
                                </button>
                            )}
                            <div className='magic-scheduler__suggestion-list' ref={suggestionListRef}>
                                {suggestionsLoading && (
                                    <div className='magic-scheduler__panel-status'>
                                        Finding nearby teacher routes…
                                    </div>
                                )}
                                {!suggestionsLoading && suggestionsError && (
                                    <div className='magic-scheduler__panel-status is-error'>
                                        {suggestionsError}
                                    </div>
                                )}
                                {!suggestionsLoading && !suggestionsError && visibleSuggestions.map((suggestion) => {
                                    const suggestionIndex = suggestions.indexOf(suggestion)
                                    const suggestionKey = getSuggestionKey(suggestion, suggestionIndex)
                                    const teacherKey = getSuggestionTeacherKey(suggestion)
                                    const targetMarkerId = suggestionTargetByKey[suggestionKey]
                                    const exactPinMatch = Boolean(
                                        hoveredMarkerId
                                        && targetMarkerId === hoveredMarkerId
                                    )
                                    return (
                                        <div
                                            className='magic-scheduler__suggestion-target'
                                            key={suggestionKey}
                                            ref={(node) => {
                                                if (node) {
                                                    suggestionBubbleRefs.current.set(suggestionKey, node)
                                                } else {
                                                    suggestionBubbleRefs.current.delete(suggestionKey)
                                                }
                                            }}
                                        >
                                            <TeacherDriveTimeBubble
                                                suggestion={suggestion}
                                                invitations={invitations}
                                                accentColor={colorMap[teacherKey]}
                                                showInstruments
                                                isHighlighted={
                                                    hoveredSuggestionKey === suggestionKey
                                                    || selectedPinSuggestionKey === suggestionKey
                                                    || calendarSelection?.suggestionKey === suggestionKey
                                                    || exactPinMatch
                                                }
                                                isSelected={calendarSelection?.suggestionKey === suggestionKey}
                                                showInvitationStatus
                                                onHoverStart={() => handleSuggestionHover(suggestion, suggestionIndex)}
                                                onHoverEnd={clearSuggestionHover}
                                                onSelect={() => handleSuggestionSelect(suggestion, suggestionIndex)}
                                            />
                                        </div>
                                    )
                                })}
                                {!suggestionsLoading && !suggestionsError && visibleSuggestions.length === 0 && (
                                    <div className='magic-scheduler__panel-status'>
                                        {availabilityMatchOn && suggestions.length > 0
                                            ? 'No teachers have availability that overlaps with this family. Turn off Availability Match to see every instrument-matched teacher.'
                                            : 'No nearby teacher suggestions were found.'}
                                    </div>
                                )}
                            </div>
                        </aside>

                    <div className={`magic-scheduler__content${activeTab === 'suggestions' ? '' : ' is-hidden'}`}>
                        <main className='magic-scheduler__map'>
                            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                                {mapsReady && !mapLoading && !mapError && (
                                    <GoogleMap
                                        style={{ width: '100%', height: '100%' }}
                                        zoom={11}
                                        center={mapCenter}
                                        gestureHandling='greedy'
                                        disableDefaultUI={false}
                                        mapId={GOOGLE_MAP_ID}
                                    >
                                        {visibleMapMarkers.map((marker) => {
                                            const isUnavailablePin = availabilityMatchOn
                                                && marker.type !== 'target-family'
                                                && !visibleSuggestionMarkerIds.has(marker.id)
                                            return (
                                                <AdvancedMarker
                                                    key={marker.id}
                                                    ref={(markerElement) => bindMarkerHover(marker.id, markerElement)}
                                                    className='magic-scheduler__advanced-marker'
                                                    position={marker.location}
                                                    title={marker.type === 'target-family'
                                                        ? `${marker.familyName} Family — family being assigned`
                                                        : isUnavailablePin
                                                            ? 'No matching family availability'
                                                            : undefined}
                                                    onClick={() => handleMarkerClick(marker.id)}
                                                >
                                                    <div
                                                        className={`magic-scheduler__marker-anchor${focusedMarker?.id === marker.id ? ' is-highlighted' : ''}${isUnavailablePin ? ' is-unavailable' : ''}`}
                                                        data-marker-id={marker.id}
                                                        onMouseEnter={() => handleMarkerHover(marker.id)}
                                                        onMouseLeave={() => clearMarkerHover(marker.id)}
                                                    >
                                                    {hoveredMarkerId === marker.id && !hoveredSuggestionKey && (
                                                        <div className='magic-scheduler__marker-hover-card'>
                                                            <strong>
                                                                {marker.type === 'teacher-home'
                                                                    ? marker.teacherName
                                                                    : `${marker.familyName} Family`}
                                                            </strong>
                                                            <span>{marker.address}</span>
                                                            {marker.type === 'teacher-home' && (
                                                                <div>
                                                                    Teacher home
                                                                    {marker.instruments?.length > 0
                                                                        ? ` · ${marker.instruments.join(', ')}`
                                                                        : ''}
                                                                </div>
                                                            )}
                                                            {marker.type === 'student-stop' && (
                                                                <>
                                                                    <div style={{ color: marker.color }}>
                                                                        {marker.teacherName}'s route
                                                                    </div>
                                                                    {marker.students.map((student) => (
                                                                        <div key={student.id}>
                                                                            {student.name}
                                                                            {student.scheduleLabels.length > 0
                                                                                ? ` · ${student.scheduleLabels.join(' • ')}`
                                                                                : ''}
                                                                        </div>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div
                                                        className='magic-scheduler__marker-pin'
                                                        style={{ opacity: isUnavailablePin ? 0.5 : 1 }}
                                                    >
                                                        <Pin
                                                            background={marker.color}
                                                            borderColor={
                                                                focusedMarker?.id === marker.id
                                                                    ? '#FACC15'
                                                                    : marker.type === 'target-family'
                                                                        ? MAGIC_SCHEDULER_FAMILY_BORDER_COLOR
                                                                        : marker.color
                                                            }
                                                            glyphColor='#FFFFFF'
                                                            glyph={
                                                                marker.type === 'target-family'
                                                                    ? '⌂'
                                                                    : marker.type === 'teacher-home' ? 'T' : undefined
                                                            }
                                                            scale={
                                                                focusedMarker?.id === marker.id
                                                                    ? marker.type === 'target-family' ? 1.5 : 1.35
                                                                    : marker.type === 'target-family' ? 1.25 : 1
                                                            }
                                                        />
                                                    </div>
                                                    </div>
                                                </AdvancedMarker>
                                            )
                                        })}
                                        {selectedMarker && (
                                            <InfoWindow
                                                position={selectedMarker.location}
                                                onCloseClick={() => setSelectedMarkerId(null)}
                                            >
                                                <div className='magic-scheduler__map-info'>
                                                    <div className='magic-scheduler__map-info-header'>
                                                        <strong>
                                                            {selectedMarker.type === 'target-family'
                                                                ? `${selectedMarker.familyName} Family`
                                                                : `${selectedMarker.familyName} Family`}
                                                        </strong>
                                                        <button
                                                            type='button'
                                                            aria-label='Close map details'
                                                            onClick={() => setSelectedMarkerId(null)}
                                                        >
                                                            <MdClose size={15} />
                                                        </button>
                                                    </div>
                                                    <span>{selectedMarker.address}</span>
                                                    {selectedMarker.type === 'student-stop' && (
                                                        <>
                                                            <div
                                                                className='magic-scheduler__map-info-teacher'
                                                                style={{ color: selectedMarker.color }}
                                                            >
                                                                {selectedMarker.teacherName}'s students
                                                            </div>
                                                            {selectedMarker.students.map((student) => (
                                                                <div className='magic-scheduler__map-info-student' key={student.id}>
                                                                    <strong>{student.name}</strong>
                                                                    <span>{student.scheduleLabels.join(' • ') || 'Lesson time unavailable'}</span>
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            </InfoWindow>
                                        )}
                                    </GoogleMap>
                                )}
                            </APIProvider>
                            {(!mapsReady || mapLoading) && (
                                <div className='magic-scheduler__map-status'>
                                    <strong>Loading teacher rosters</strong>
                                    <span>Mapping the students for every suggested teacher.</span>
                                </div>
                            )}
                            {mapsReady && !mapLoading && mapError && (
                                <div className='magic-scheduler__map-status is-error'>{mapError}</div>
                            )}
                        </main>

                        <aside className='magic-scheduler__family-panel'>
                            <section className='magic-scheduler__family-section'>
                                <h3>Family Availability</h3>
                                <div className='magic-scheduler__availability-grid'>
                                    <span aria-hidden='true' />
                                    {AVAILABILITY_PERIODS.map((period) => (
                                        <span className='magic-scheduler__availability-period' key={period}>
                                            {period}
                                        </span>
                                    ))}
                                    {AVAILABILITY_DAYS.map((day) => (
                                        <React.Fragment key={day}>
                                            <span className='magic-scheduler__availability-day'>
                                                {day.slice(0, 3)}
                                            </span>
                                            {AVAILABILITY_PERIODS.map((period) => {
                                                const checked = familyAvailability?.[day]?.[period] === true
                                                return (
                                                    <span
                                                        className={`magic-scheduler__availability-checkbox${checked ? ' is-checked' : ''}`}
                                                        role='checkbox'
                                                        aria-checked={checked}
                                                        aria-readonly='true'
                                                        aria-label={`${day} ${period}`}
                                                        key={period}
                                                    >
                                                        {checked && <MdCheck size={15} aria-hidden='true' />}
                                                    </span>
                                                )
                                            })}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </section>

                            <section className='magic-scheduler__family-section magic-scheduler__students'>
                                {studentList.map((student) => (
                                    <StudentBadge
                                        key={student.studentId || student.studentName}
                                        student={student}
                                        familyInfo={safeFamilyProfile}
                                        scheduledEvents={[]}
                                    />
                                ))}
                            </section>

                            {familyMessage && (
                                <section className='magic-scheduler__family-section magic-scheduler__message'>
                                    <h3>Message</h3>
                                    <p>{familyMessage}</p>
                                </section>
                            )}
                            {!familyMessage && studentList.length === 0 && (
                                <div className='magic-scheduler__family-empty'>
                                    <MdOutlineSchool size={22} />
                                    No family details are available yet.
                                </div>
                            )}
                        </aside>
                    </div>
                {hasOpenedCalendar && (
                    <div className={`magic-scheduler__calendar-tab${activeTab === 'calendar' ? '' : ' is-hidden'}`}>
                        <MagicSchedulerCalendar
                            suggestions={availabilityFilteredSuggestions}
                            teachers={teachers}
                            teacherColorMap={colorMap}
                            familyAvailability={familyAvailability}
                            familyAddress={familyAddress}
                            familyId={modalInfo?.familyId}
                            familyName={familyName}
                            familyProfile={safeFamilyProfile}
                            schoolId={modalInfo?.whichSchool}
                            socket={socket}
                            userData={userData}
                            availabilityMatchOn={availabilityMatchOn}
                            onToggleAvailabilityMatch={toggleAvailabilityMatch}
                            preferredTeacherKey={calendarSelection?.teacherKey || filteredTeacherKey}
                            highlightedSuggestion={calendarSelection?.suggestion}
                            showTeacherList={false}
                        />
                    </div>
                )}
                </div>
            </div>
        </div>
    )
}

export default MagicScheduler
